import { getWebInstrumentations, initializeFaro, ReactIntegration } from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

import { APP_NAME } from './brand';

/**
 * Browser telemetry, posted to the cluster's Faro receiver (Alloy), which
 * forwards traces to Tempo and console output and errors to Loki.
 *
 * Inert unless a collector is configured: a local checkout and the end-to-end
 * suite must not post anywhere. The URL arrives through the runtime config, so
 * one bundle works in every environment.
 *
 * It is the full collect endpoint, not a base: Faro hands it straight to its
 * transport, which does not append /collect.
 */
const runtimeConfig = typeof window !== 'undefined' ? window.__CASHIER_CONFIG__ : undefined;
const collectorUrl = runtimeConfig?.faroUrl?.trim();

/**
 * Whether Faro will actually start.
 *
 * FaroErrorBoundary calls api.pushError unguarded, and that api only exists
 * once ReactIntegration has been constructed by initializeFaro -- so rendering
 * the boundary without a collector throws inside the error handler itself.
 */
export const faroEnabled = Boolean(collectorUrl);

/**
 * Origin of a separately-hosted API, or undefined when it is same-origin.
 *
 * Same-origin requests always carry traceparent. A cross-origin one only does
 * when its origin is listed, so a dev backend on 127.0.0.1:8000 would otherwise
 * start its own trace instead of continuing the browser's.
 */
const crossOriginApi = (() => {
  const configured = runtimeConfig?.apiBaseUrl;
  if (!configured || configured === '/') return undefined;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return undefined;
  }
})();

/**
 * A pattern matching every URL on `origin`.
 *
 * The OTel fetch instrumentation compares each candidate against the whole
 * request URL, and for a string that comparison is exact equality (urlMatches in
 * @opentelemetry/core). A bare origin therefore never matches
 * "https://api.example.com/api/...", so the traceparent was dropped for every
 * cross-origin API call and the backend started its own trace instead. Only a
 * pattern anchored on the origin actually propagates.
 */
function originPattern(origin: string): RegExp {
  return new RegExp(`^${origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:/|$)`);
}

const propagateTraceHeaderCorsUrls = crossOriginApi ? [originPattern(crossOriginApi)] : [];

let started: ReturnType<typeof initializeFaro> | undefined;

export function initFaro(): void {
  if (!collectorUrl || started) return;

  started = initializeFaro({
    url: collectorUrl,
    app: {
      name: APP_NAME,
      environment: import.meta.env.MODE,
    },
    // Batches are JSON; compressing them matters on a stall's mobile link.
    requestCompression: true,
    // NavigationInstrumentation is part of the web set but stays silent unless
    // this is set, so navigation telemetry is lost by default.
    experimental: { trackNavigation: true },
    // Both are platform noise we cannot act on: a ResizeObserver loop warning
    // is a browser quirk, and a bare "Script error." is a cross-origin error
    // with no detail. Reporting them buries real errors.
    ignoreErrors: [/^ResizeObserver loop/, /^Script error\.?$/],
    instrumentations: [
      // Console output, unhandled errors, web vitals and session tracking.
      ...getWebInstrumentations(),
      // Wires FaroErrorBoundary. The app is client-rendered and has no router,
      // so the React Router integrations are not applicable.
      new ReactIntegration(),
      new TracingInstrumentation({
        // Nested, not top-level: the Cloud docs show the flat form, which the
        // installed 2.12.1 types do not accept.
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls,
        },
      }),
    ],
  });
}

/**
 * Attribute telemetry to the person signed in on this device.
 *
 * A till is shared, so this is called on every sign-in rather than once per
 * browser, and clearUser on the way out.
 */
export function identifyUser(username: string | null): void {
  if (!started || !username) return;
  started.api.setUser({ username });
}

/** Stop attributing events to the account that just left the device. */
export function clearUser(): void {
  started?.api.resetUser();
}

/** Record which screen the till is on, so an error arrives with its context. */
export function setView(view: string): void {
  if (!started || !view) return;
  started.api.setView({ name: view });
}
